import { Button, Divider, Grid } from "@mui/material";
import * as React from "react";
import {
    BitrateMode,
    IngestProtocol,
    IngestType,
    ResolutionType,
    TLAHardewareInterfaceGstDecklinkModesType,
    TLAHardewareInterfaceGstDecklinkProfileIdType,
    TLAHardewareInterfaceGstDecklinkVideoFormatType,
    TLAHardwareInterfaceDecklinkConnectionType,
} from "../../../__generated__/globalTypes";
import CreateChannelFormBasics from "./CreateChannelFormBasics";
import styled from "styled-components";

import { useMutation } from "react-apollo";
import { GetProfiles_profiles } from "../../AdminConfig/Profiles/__generated__/GetProfiles";
import { CREATE_CHANNEL_MUTATION, GET_CHANNELS_ORGANIZATION_QUERY } from "../Overview/channelQueriesMutations";
import { CreateChannelMutation, CreateChannelMutationVariables } from "../Overview/__generated__/CreateChannelMutation";
import { toast, ToastContainer } from "react-toastify";
import { TOAST_SETTINGS } from "../../BillingDetails/AddPaymentMethodDialog";
import { navigate } from "@reach/router";
import _ from "lodash";
import ChannelStreamConfigurator, {
    determineSelectedFpsAfterBitrateOrResolutionChange,
    determineSelectedResolutionAfterBitrateChange,
    getFpsValues,
    StreamConfigType,
} from "./ChannelStreamConfigurator";
import StreamConfigurationInformation from "./StreamConfigurationInformation";
import { GetOrganizationChannelSettingsQuery_organization } from "../../../views/App/__generated__/GetOrganizationChannelSettingsQuery";
import CreateChannelMiscellaneousSettings, {
    CreateChannelMiscellaneousType,
} from "./CreateChannelMiscellaneousSettings";
import { THEOadsAssetKey } from "../THEOadsAssetKeys";
import { v4 } from "uuid";
import { NFL_ORG_ID } from "../../AppSidebar/OrganizationSidebar";
import AuthService from "../../../auth/AuthService";
import {ReStreamDestination} from "./ChannelRestreamDestinations";

interface Props {
    organization: GetOrganizationChannelSettingsQuery_organization;
    profiles: GetProfiles_profiles[];
}

export interface CreateChannelInput {
    name: string;
    externalId?: string;
    ingestLocation?: string;
    streamConfig: {
        fps: number;
        resolution: ResolutionType;
        bitrate: number;
        abr: boolean;
        drm: boolean;
        bitrateMode: BitrateMode;
        publishUrl: string;
        restream: ReStreamDestination[]; 
        skipTranscode: boolean;
    };
    perpetual: boolean;
    autoStop: boolean;
    tla: boolean;
    theoads: {
        enabled: boolean;
        assetKeys: THEOadsAssetKey[];
        podDuration: number;
    };
    dataCenterId?: string;
    ingestPullUrl?: string;
    ingestProtocol?: IngestProtocol;
    ingestType?: IngestType;
    sdi?: ChannelSdiArgs;
}

export interface ChannelSdiArgs {
    persistentId: string;
    deviceNumber: number;
    audioConnection: TLAHardwareInterfaceDecklinkConnectionType;
    videoConnection: TLAHardwareInterfaceDecklinkConnectionType;
    videoFormat: TLAHardewareInterfaceGstDecklinkVideoFormatType;
    videoMode: TLAHardewareInterfaceGstDecklinkModesType;
    profile: TLAHardewareInterfaceGstDecklinkProfileIdType;
}

const SectionTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
`;

export default function CreateChannelForm ({ organization, profiles }: Props) {
    const allBitrates = _.uniq(profiles.map(p => p.maxBitrate)).sort((a, b) => (a > b ? -1 : 1));
    const allResolutions = _.uniq(profiles.map(p => p.maxResolution)).sort(resolutionSort);
    const fpses = _.uniq(profiles.map(p => p.maxFps));
    const allFpsValues = getFpsValues(fpses);

    const initBitrate = 4.5;
    const initResolution = ResolutionType.r_1080p;
    const initFps = 30;
    const initAbr = true;
    const initDrm = false;
    const initBitrateMode: BitrateMode = BitrateMode.constant;
    const initPublishUrl: string = "";
    const initDestinations : ReStreamDestination[] = []
    const initSkipTranscode: boolean = false

    const { organizationId, channelSettings } = organization;

    const [values, setValues] = React.useState<CreateChannelInput>({
        streamConfig: {
            bitrate: initBitrate,
            fps: initFps,
            resolution: initResolution,
            abr: initAbr,
            drm: initDrm,
            bitrateMode: initBitrateMode,
            publishUrl: initPublishUrl,
            restream: initDestinations,
            skipTranscode: initSkipTranscode
        },
        ingestLocation: "europe-west",
        dataCenterId: organization.tla?.datacenters?.length > 0 ? organization.tla.datacenters[0].id : undefined,
        name: "",
        autoStop: true,
        perpetual: false,
        tla: false,
        theoads: {
            enabled: false,
            assetKeys: [{ adBreakSelectionParameters: [], assetKey: "", eabnAdTagParameters: [], tempId: v4() }],
            podDuration: 30,
        },
        ingestProtocol: IngestProtocol.rtmp,
        ingestPullUrl: "",
        externalId: "",
    });


    function constructIngestPullUrl(ingestUrl: string, ingestProtocol : IngestProtocol) : string {
        if(ingestProtocol === IngestProtocol.ndi && !ingestUrl.startsWith("ndi://")) {
            return `ndi://q?url=${ingestUrl}`;
        }
        return ingestUrl;
    }

    const [firstSubmitDone, setFirstSubmitDone] = React.useState<boolean>(false);

    const [createChannelMut, { loading: loadingCreate }] = useMutation<
        CreateChannelMutation,
        CreateChannelMutationVariables
    >(CREATE_CHANNEL_MUTATION);

    async function onSubmitClick () {
        setFirstSubmitDone(true);
        const {
            streamConfig,
            ingestLocation,
            name,
            autoStop,
            perpetual,
            tla,
            dataCenterId,
            ingestProtocol,
            ingestPullUrl,
            sdi,
            theoads,
            externalId,
            ingestType,
        } = values;

        if (isValidInput()) {
            try {
                const createdChannel = await createChannelMut({
                    variables: {
                        input: {
                            metadata: {
                                name,
                                externalId:
                                    externalId && externalId.trim().length > 0 && organizationId === NFL_ORG_ID
                                        ? externalId
                                        : undefined,
                            },
                            ingestLocation: tla ? undefined : ingestLocation,
                            streamConfig,
                            organizationId,
                            ingestTimeout: autoStop ? 3600 : 0,
                            ...(theoads.enabled === true && {
                                theoads: {
                                    enabled: true,
                                    podDuration: theoads.podDuration,
                                    assetKeys: theoads.assetKeys.map(ak => ({
                                        assetKey: ak.assetKey,
                                        adBreakSelectionParameters: ak.adBreakSelectionParameters.map(adb => ({
                                            key: adb.key,
                                            value: adb.value,
                                        })),
                                        eabnAdTagParameters: ak.eabnAdTagParameters.map(adb => ({
                                            key: adb.key,
                                            value: adb.value,
                                        })),
                                    })),
                                },
                            }),

                            ...(channelSettings.perpetual.enabled === true && { perpetual }),
                            ...(tla === true && {
                                datacenterId: dataCenterId!,
                                ingestProtocol: ingestProtocol!,
                                ingestType: ingestType ?? IngestType.pull,
                                ...(ingestType !== IngestType.push && { ingestPullUrl: constructIngestPullUrl(ingestPullUrl!, ingestProtocol!) }),
                                ...(ingestProtocol === IngestProtocol.sdi && { sdi }),
                                // hardwareInterface, // TODO: sdi here?
                            }),
                        },
                    },
                    refetchQueries: [
                        {
                            query: GET_CHANNELS_ORGANIZATION_QUERY,
                            variables: {
                                organizationId,
                            },
                        },
                    ],
                });
                navigate(`/app/${organizationId}/channels/${createdChannel.data?.createChannel.channelId}`);
            } catch (e: any) {
                toast.error(e.graphQLErrors[0].message ?? "Something went wrong 😞", TOAST_SETTINGS);
            }
        }
    }

    function isValidInput () {
        if (values.name.trim() === "") {
            return false;
        }

        if (values.tla === true) {
            if (values.ingestProtocol === IngestProtocol.sdi && values.sdi?.persistentId) {
                return true;
            }
            if (
                values.ingestProtocol !== IngestProtocol.rtmp ||
                (values.ingestProtocol === IngestProtocol.rtmp && values.ingestType !== IngestType.push)
            )
                if (!values.ingestPullUrl || values.ingestPullUrl.trim().length === 0) {
                    return false;
                }
        }

        if (values.theoads.enabled === true) {
            if (values.theoads.assetKeys.length === 0) {
                return false;
            }

            const uniqueKeys =
                _.uniq(values.theoads.assetKeys.map(ak => ak.assetKey)).length === values.theoads.assetKeys.length;

            if (!uniqueKeys) {
                return false;
            }

            if (values.theoads.podDuration < 0) {
                return false;
            }
        }

        return true;
    }

    function onChangeConfigValue (type: StreamConfigType, value: string | number | ResolutionType | boolean | ReStreamDestination[]) {
        switch (type) {
            case "bitratemode":
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        bitrateMode: value as BitrateMode,
                    },
                });
                break;
            case "bitrate":
                onBitrateChange(value as number);
                break;
            case "resolution":
                onResolutionChanged(value as ResolutionType);
                break;
            case "abr":
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        abr: value as boolean,
                    },
                });
                break;
            case "drm":
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        drm: value as boolean,
                    },
                });
                break;
            case "fps":
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        fps: value as number,
                    },
                });
                break;
            case "publishurl":
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        publishUrl: value as string,
                    },
                });
                break;
            case "restream":
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        restream: value as ReStreamDestination[],
                    },
                });
                break;
            case "skiptranscode": {
                setValues({
                    ...values,
                    streamConfig: {
                        ...values.streamConfig,
                        skipTranscode: value as boolean
                    }
                })

            }
        }
    }

    function onBitrateChange (value: number) {
        const newBitrate = value as number;
        const newResolution = determineSelectedResolutionAfterBitrateChange(
            profiles,
            newBitrate,
            values.streamConfig.resolution,
        );
        const newFps = determineSelectedFpsAfterBitrateOrResolutionChange(
            profiles,
            newBitrate,
            newResolution,
            values.streamConfig.fps,
        );
        setValues({
            ...values,
            streamConfig: {
                ...values.streamConfig,
                bitrate: newBitrate,
                fps: newFps,
                resolution: newResolution,
            },
        });
    }

    function onResolutionChanged (value: ResolutionType) {
        const newResolution = value;
        const newFps = determineSelectedFpsAfterBitrateOrResolutionChange(
            profiles,
            values.streamConfig.bitrate,
            newResolution,
            values.streamConfig.fps,
        );
        setValues({
            ...values,
            streamConfig: {
                ...values.streamConfig,
                fps: newFps,
                resolution: newResolution,
            },
        });
    }

    const selectedProfile = profiles.find(
        p =>
            p.maxBitrate === values.streamConfig.bitrate &&
            p.maxFps === getMaxFps(values.streamConfig.fps) &&
            p.maxResolution === values.streamConfig.resolution,
    );

    function getMaxFps (val: number) {
        return val <= 30 ? 30 : 60;
    }

    function onChangeDC (dcId: string) {
        // const dcHasSdi =
        //     _.flatMap(
        //         organization.tla.datacenters
        //             .find((dc) => dc.id === dcId)!
        //             .tla.deviceContexts.map((ctx) => ctx.sdiInputs)
        //             .filter((inp) => inp !== null),
        //     ).length > 0;

        // if (values.ingestProtocol === IngestProtocol.sdi) {
        //     const hwInterfaces =
        //         organization.tla?.datacenters?.find((dc) => dc.id === dcId)?.tla.hardwareInterfaces ?? [];

        //     setValues({
        //         ...values,
        //         dataCenterId: dcId,
        //         hardwareInterface: {
        //             hwiId: hwInterfaces[0].id ?? "",
        //             persistentId: hwInterfaces[0].inputs[0].persistentId ?? "",
        //             audioConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
        //             profile: TLAHardewareInterfaceGstDecklinkProfileIdType.Default,
        //             videoConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
        //             videoFormat: TLAHardewareInterfaceGstDecklinkVideoFormatType.Auto,
        //             videoMode: TLAHardewareInterfaceGstDecklinkModesType.Auto,
        //         },
        //     });
        // } else {
        setValues({ ...values, dataCenterId: dcId, ingestProtocol: IngestProtocol.rtmp });
        // }
    }

    function canConfigureOnPrem (): boolean {
        const isAdmin = AuthService.isAdmin() ?? false;
        // check if org is connected to TLA
        return isAdmin && organization.tla.activated && values.tla;
    }

    function onChangeIngestProtocol (protocol: IngestProtocol, ingestType?: IngestType) {
        if (protocol === IngestProtocol.sdi) {
            const sdiInputsForDC = _.flatMap(
                organization.tla.datacenters
                    .find(dc => dc.id === values.dataCenterId)!
                    .tla.deviceContexts.map(ctx => ctx.sdiInputs)
                    .filter(inp => inp !== null),
            );
            if (sdiInputsForDC && sdiInputsForDC.length > 0) {
                setValues({
                    ...values,
                    sdi: {
                        ...values.sdi,
                        persistentId:
                            sdiInputsForDC && sdiInputsForDC.length > 0 ? sdiInputsForDC![0]!.persistentId : "",
                        deviceNumber:
                            sdiInputsForDC && sdiInputsForDC.length > 0 ? sdiInputsForDC![0]!.deviceNumber : 0,
                        audioConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                        profile: TLAHardewareInterfaceGstDecklinkProfileIdType.Default,
                        videoConnection: TLAHardwareInterfaceDecklinkConnectionType.Auto,
                        videoFormat: TLAHardewareInterfaceGstDecklinkVideoFormatType.Auto,
                        videoMode: TLAHardewareInterfaceGstDecklinkModesType.Auto,
                    },
                    ingestProtocol: protocol,
                });
            }
        } else {
            setValues({
                ...values,
                ingestProtocol: protocol,
                ingestType: ingestType ?? IngestType.pull,
            });
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <SectionTitle>Basic information</SectionTitle>
                <CreateChannelFormBasics
                    organization={organization}
                    firstSubmitDone={firstSubmitDone}
                    input={values}
                    onChangeTLA={() => setValues({ ...values, tla: !values.tla })}
                    onNameChange={(val: string) => setValues({ ...values, name: val })}
                    onExternalIdChange={(val: string) => setValues({ ...values, externalId: val })}
                    onChangePullUrl={(val: string) => setValues({ ...values, ingestPullUrl: val })}
                    onLocationChange={(val: string) => setValues({ ...values, ingestLocation: val })}
                    onDatacenterChange={(val: string) => onChangeDC(val)}
                    onChangeSdi={(sdi: ChannelSdiArgs) => setValues({ ...values, sdi })}
                    onChangeIngestProtocol={(protocol: IngestProtocol, type?: IngestType) => {
                        onChangeIngestProtocol(protocol, type);
                    }}
                    onChangeTHEOads={(enabled: boolean, assetKeys: THEOadsAssetKey[], podDuration: number) =>
                        setValues({ ...values, theoads: { enabled, assetKeys, podDuration } })
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <SectionTitle>Stream configuration</SectionTitle>
                <ChannelStreamConfigurator
                    abr={values.streamConfig.abr}
                    bitrateMode={values.streamConfig.bitrateMode}
                    allBitrates={allBitrates}
                    allFpsValues={allFpsValues}
                    allResolutions={allResolutions}
                    selectedBitrate={values.streamConfig.bitrate}
                    selectedResolution={values.streamConfig.resolution}
                    selectedFps={values.streamConfig.fps}
                    profiles={profiles}
                    restreamDestinations={values.streamConfig.restream}
                    onChange={(type: StreamConfigType, value: string | number | ResolutionType | boolean | ReStreamDestination[])  =>
                        onChangeConfigValue(type, value)
                    }
                    drm={values.streamConfig.drm}
                    organizationId={organization.organizationId}
                    hasAccessToDrm={organization.channelSettings?.drm?.enabled === true}
                    canConfigureOnPrem={canConfigureOnPrem()}
                    skipTranscode={values.streamConfig.skipTranscode}
                />
            </Grid>
            <Grid item xs={12}>
                <StreamConfigurationInformation
                    drm={values.streamConfig.drm}
                    abr={values.streamConfig.abr}
                    profile={selectedProfile!}
                />
            </Grid>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <SectionTitle>Miscellaneous</SectionTitle>
                <CreateChannelMiscellaneousSettings
                    channelSettings={channelSettings}
                    values={{ autoStop: values.autoStop, perpetual: values.perpetual }}
                    onChangeBoolean={(type: CreateChannelMiscellaneousType) => {
                        setValues({
                            ...values,
                            ...(type === "autoStop" && { autoStop: !values.autoStop }),
                            ...(type === "perpetual" && { perpetual: !values.perpetual }),
                        });
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                    {}
                    <Button
                        onClick={onSubmitClick}
                        variant='contained'
                        size='large'
                        disabled={loadingCreate || !isValidInput()}
                    >
                        {loadingCreate ? "Creating..." : "Create"}
                    </Button>
                </div>
            </Grid>
            <ToastContainer />
        </Grid>
    );
}

export function resolutionSort (a: ResolutionType, b: ResolutionType) {
    const resOrder: Record<ResolutionType, number> = {
        r_1080p: 0,
        r_720p: 1,
        r_576p: 2,
        r_360p: 3,
        r_240p: 4,
    };

    if (resOrder[a] < resOrder[b]) {
        return -1;
    } else if (a === b) {
        return 0;
    } else {
        return 1;
    }
}
